/* ═══════════════════════════════════════════════════════════════
   NAV — the dashboard's query string, both ways.

   `?tab=<key>` has been the dashboard's deep link since Sprint A.
   People & vehicles (design 2026-09-08 §3) adds the first URL a
   person or a truck has ever had:

     ?tab=people&driver=<driver_id>
     ?tab=people&vehicle=<number plate, no spaces, upper>

   Pure: no window, no history. TachoDashboard reads once on mount
   with parseNavQuery and writes back with buildNavQuery through
   history.replaceState. The tab key is passed through as given —
   the dashboard validates it against its own tab list.
   ═══════════════════════════════════════════════════════════════ */

import { normVrn } from './actionQueueItems.js';

const PARAM_TAB = 'tab';
const PARAM_DRIVER = 'driver';
const PARAM_VEHICLE = 'vehicle';

const EMPTY = Object.freeze({ tab: null, driverId: null, vrn: null });

function toParams(search) {
  if (search instanceof URLSearchParams) return search;
  if (typeof search !== 'string') return null;
  try {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return null;
  }
}

const clean = (s) => (typeof s === 'string' && s.trim() ? s.trim() : null);

/**
 * `'?tab=people&driver=abc'` → `{ tab: 'people', driverId: 'abc', vrn: null }`.
 * Accepts a string with or without the leading '?', or a URLSearchParams.
 * A driver or a vehicle with no tab implies the people tab. Never throws;
 * `{ tab: null, driverId: null, vrn: null }` on nothing.
 */
export function parseNavQuery(search) {
  const params = toParams(search);
  if (!params) return { ...EMPTY };
  const driverId = clean(params.get(PARAM_DRIVER));
  const vrn = normVrn(params.get(PARAM_VEHICLE)) || null;
  const tab = clean(params.get(PARAM_TAB)) || (driverId || vrn ? 'people' : null);
  return { tab, driverId, vrn };
}

/**
 * `{ tab, driverId, vrn }` → `'tab=people&driver=abc'` (no leading '?').
 * Other params in `base` (a search string, with or without '?') are kept
 * in place; the three nav params are replaced. A driver or a vehicle with
 * no tab implies the people tab. '' on nothing.
 */
export function buildNavQuery(nav, base = '') {
  const params = toParams(base) || new URLSearchParams();
  params.delete(PARAM_TAB);
  params.delete(PARAM_DRIVER);
  params.delete(PARAM_VEHICLE);

  const n = nav && typeof nav === 'object' ? nav : {};
  const driverId = clean(n.driverId);
  const vrn = normVrn(n.vrn) || null;
  const tab = clean(n.tab) || (driverId || vrn ? 'people' : null);

  if (tab) params.set(PARAM_TAB, tab);
  if (driverId) params.set(PARAM_DRIVER, driverId);
  if (vrn) params.set(PARAM_VEHICLE, vrn);
  return params.toString();
}
